import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import ConfirmDialog from './ConfirmDialog';

const CONNECT_LABELS = {
  ready: { text: 'Connected', className: 'text-green-700' },
  onboarding: { text: 'Onboarding', className: 'text-yellow-700' },
  not_connected: { text: 'Not connected', className: 'text-gray-400' }
};

const ModeBadge = ({ mode }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
      mode === 'live' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
    }`}
  >
    {mode === 'live' ? 'LIVE' : 'TEST'}
  </span>
);

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

// Per-organization Stripe test/live switch (app managers only). An organization
// in live mode takes real payments through its live Stripe Connect account.
const OrganizationStripeModes = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [pendingLive, setPendingLive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/stripe-modes', { headers: authHeaders() });
      const body = await res.json();
      if (!res.ok) throw new Error(body.message || 'Failed to load Stripe modes');
      setData(body);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveMode = async (org, mode) => {
    setSavingId(org.id);
    try {
      const res = await fetch(`/api/admin/organizations/${org.id}/stripe-mode`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ mode })
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.message || 'Failed to update Stripe mode');
      setData(prev => ({
        ...prev,
        organizations: prev.organizations.map(o =>
          o.id === org.id ? { ...o, stripeMode: body.stripeMode, effectiveMode: body.effectiveMode } : o
        )
      }));
      toast.success(`${org.name} now uses Stripe ${body.effectiveMode} mode`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingId(null);
    }
  };

  const handleChange = (org, value) => {
    const mode = value === 'default' ? null : value;
    const effective = mode || data.defaultMode;
    // Going live means real money - always confirm.
    if (effective === 'live' && org.effectiveMode !== 'live') {
      setPendingLive({ org, mode });
      return;
    }
    saveMode(org, mode);
  };

  if (loading) {
    return <div className="text-sm text-gray-500">Loading organization payment modes…</div>;
  }
  if (!data) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h4 className="text-md font-semibold text-gray-900">Payment Mode by Organization</h4>
          <p className="text-sm text-gray-600 mt-1">
            Choose whether each organization takes test or real (live) payments. Each mode needs its own
            Stripe Connect setup by the organization. Default for organizations without a setting:{' '}
            <ModeBadge mode={data.defaultMode} />
          </p>
        </div>
        <button
          onClick={load}
          className="text-sm text-blue-600 hover:text-blue-800 whitespace-nowrap"
        >
          Refresh
        </button>
      </div>

      {!data.modesConfigured.live && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
          Live Stripe keys aren't configured on the server, so organizations can't be switched to live yet.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-200">
              <th className="py-2 pr-4 font-medium">Organization</th>
              <th className="py-2 pr-4 font-medium">Mode</th>
              <th className="py-2 pr-4 font-medium">Test account</th>
              <th className="py-2 pr-4 font-medium">Live account</th>
              <th className="py-2 font-medium">Setting</th>
            </tr>
          </thead>
          <tbody>
            {data.organizations.map(org => {
              const liveNotReady = org.effectiveMode === 'live' && org.connect.live !== 'ready';
              return (
                <tr key={org.id} className="border-b border-gray-100 last:border-0">
                  <td className="py-3 pr-4 text-gray-900">
                    {org.name}
                    {liveNotReady && (
                      <div className="text-xs text-red-600 mt-0.5">
                        Live, but the organization hasn't finished connecting a live Stripe account. Card payments are unavailable until it does.
                      </div>
                    )}
                  </td>
                  <td className="py-3 pr-4"><ModeBadge mode={org.effectiveMode} /></td>
                  {['test', 'live'].map(mode => (
                    <td key={mode} className={`py-3 pr-4 ${CONNECT_LABELS[org.connect[mode]].className}`}>
                      {CONNECT_LABELS[org.connect[mode]].text}
                    </td>
                  ))}
                  <td className="py-3">
                    <select
                      value={org.stripeMode || 'default'}
                      disabled={savingId === org.id}
                      onChange={e => handleChange(org, e.target.value)}
                      className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white"
                    >
                      <option value="default">Default ({data.defaultMode})</option>
                      <option value="test">Test</option>
                      <option value="live" disabled={!data.modesConfigured.live}>Live</option>
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={!!pendingLive}
        title="Switch to live payments?"
        message={pendingLive
          ? `${pendingLive.org.name} will start charging real cards. ${
              pendingLive.org.connect.live === 'ready'
                ? 'Its live Stripe account is connected.'
                : 'It has not finished connecting a live Stripe account yet, so card payments will be unavailable until an organization admin connects Stripe again.'
            } Saved auto-pay cards from test mode won't carry over.`
          : ''}
        confirmLabel="Go live"
        isDangerous
        onCancel={() => setPendingLive(null)}
        onConfirm={() => {
          const { org, mode } = pendingLive;
          setPendingLive(null);
          saveMode(org, mode);
        }}
      />
    </div>
  );
};

export default OrganizationStripeModes;
